// Mirrors the server rule in EvaluationSessionSerializer (MIN_CHATLOGS = 6, i.e. trainee +
// customer per turn). Client-side this only greys out the obviously-too-short case; the server
// stays authoritative and returns 400 with the same message if the two ever disagree.
//
// Kept in its own module so it can be unit-tested without pulling in AudioOrbOpenAI's three.js
// visual dependencies.
export const MIN_TURNS_FOR_EVALUATION = 3;

export const canRequestEvaluation = userTurns =>
  userTurns >= MIN_TURNS_FOR_EVALUATION;

// Points earned on one criterion: its full score if met, else 0 — the evaluator's confidence
// is not a factor (#215). Mirrors EvaluatedCriterion.points() on the server.
export const criterionPoints = ec =>
  ec.result && ec.result.is_criterion_met ? ec.criterion.score : 0;

// Scoring is criterion.score if is_criterion_met, so on a negative-score
// (deduction) criterion met=true means the deduction APPLIED — a bad outcome. Every display of
// is_criterion_met goes through here so that polarity lives in one place.
export const criterionOutcome = (score, met) => {
  const deduction = score < 0;
  if (deduction) {
    return met
      ? { deduction, favorable: false, label: "감점 적용", sentence: "감점 기준에 해당합니다." }
      : { deduction, favorable: true, label: "감점 없음", sentence: "감점 기준에 해당하지 않습니다." };
  }
  return met
    ? { deduction, favorable: true, label: "충족", sentence: "기준이 충족되었습니다." }
    : { deduction, favorable: false, label: "미충족", sentence: "기준이 충족되지 않았습니다." };
};

// Same, for an evaluatedcriterion ({ criterion, result }). No result yet → null.
export const evaluatedOutcome = ec =>
  ec.result ? criterionOutcome(ec.criterion.score, !!ec.result.is_criterion_met) : null;

// The numbers evaluation text cites are the grader's ids for trainee lines, from 0
// (api.tasks.build_judge_transcript). Each criterion stores its id -> ChatLog pk map as
// result.idx2clid, identical across one evaluation, so the transcript can label each bubble
// without regrading. Since #234 there is one id per bubble ("말풍선 12"); evaluations graded
// before that numbered spaCy sentences, so an old bubble of two sentences shows "10–11".
export const utteranceLabels = evaluatedCriteria => {
  const ids = {};
  (evaluatedCriteria || []).forEach(ec => {
    Object.entries(ec.result?.idx2clid || {}).forEach(([idx, clid]) => {
      (ids[clid] = ids[clid] || new Set()).add(Number(idx));
    });
  });
  const labels = {};
  Object.entries(ids).forEach(([clid, set]) => {
    const lo = Math.min(...set);
    const hi = Math.max(...set);
    labels[clid] = lo === hi ? `${lo}` : `${lo}–${hi}`;
  });
  return labels;
};

// What the ids in an evaluation count: bubbles (result.numbering === "bubble", since #234) or,
// in evaluations graded before that, sentences inside bubbles.
export const utteranceUnit = evaluatedCriteria =>
  (evaluatedCriteria || []).some(ec => ec.result?.numbering === "bubble") ? "말풍선" : "문장";

// Colour of criterion numbers (the summary pane's 평가 기준 chips and each 응대 스킬 card's
// number). Blue belongs to 말풍선, so criteria must not use it.
export const CRITERION_ORANGE = '#E07800';

// Radar-chart labels. connect/discover/share/close are Apple's own stage names and the
// client's user guide (docs/training/user_guide_ko.md) uses them as-is, so they stay and
// gain a short Korean gloss from the guide's definitions. Arrays render as two lines.
const TAG_LABELS = {
  connect: ['Connect', '신뢰 형성'],
  discover: ['Discover', '니즈 파악'],
  share: ['Share', '제품 소개'],
  close: ['Close', '마무리'],
  overall: ['Overall', '전반'],
  uplus: ['U+', '요금·결합'],
};
export const tagLabel = tag => TAG_LABELS[tag] || tag;

// Cross-tab jumps on phones, where only one report tab is mounted at a time: a link whose
// target isn't on screen asks the report to show it (ChatSessionReport, EvaluatedCriteria).
export const SHOW_CHATLOG_EVENT = 'report:show-chatlog';
export const SHOW_CRITERION_EVENT = 'report:show-criterion';

/** Scroll to an element that may not be rendered yet (tab switch, filter change). */
export const scrollToWhenReady = (id, tries = 20) => {
  const item = document.getElementById(id);
  if (item) {
    item.scrollIntoView({ behavior: 'smooth', block: 'center' });
    item.classList.add('highlight');
    setTimeout(() => item.classList.remove('highlight'), 1000);
  } else if (tries > 0) {
    setTimeout(() => scrollToWhenReady(id, tries - 1), 150);
  }
};
